import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface RevealProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  delay?: number;
  distance?: number;
  threshold?: number;
  once?: boolean;
}

const Frame = styled.div<{ $visible: boolean; $delay: number; $distance: number }>`
  display: block;
  width: 100%;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translate3d(0, ${({ $visible, $distance }) => ($visible ? 0 : $distance)}px, 0);
  transition:
    opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }) => `${$delay}ms`};
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

const Reveal: React.FC<RevealProps> = ({
  children,
  delay = 0,
  distance = 26,
  threshold = 0.18,
  once = true,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const node = ref.current;

    if (!node) {
      return undefined;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          if (once) {
            observer.disconnect();
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -12% 0px',
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [once, threshold]);

  return (
    <Frame ref={ref} $visible={isVisible} $delay={delay} $distance={distance} {...rest}>
      {children}
    </Frame>
  );
};

export default Reveal;